import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { DriverData } from './types';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';

interface Education {
  id: number;
  educationSchoolId?: number;
  schoolName?: string;
  programTitle: string;
  completedDate: string;
  fileId?: number;
  file?: any;
}

interface EducationTabProps {
  driverData: DriverData | null;
  educationSchools?: any[];
  onRefresh?: () => void;
}

export const EducationTab: React.FC<EducationTabProps> = ({ driverData, educationSchools = [], onRefresh }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState<Education | null>(null);
  const [formData, setFormData] = useState({
    educationSchoolId: '',
    programTitle: '',
    completedDate: '',
  });
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [viewingImage, setViewingImage] = useState<Education | null>(null);

  const educationRecords = (driverData?.education || []) as Education[];

  // Image viewer state
  const imageGalleryRef = useRef<any>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openImageViewer = (edu: Education) => {
    if (!edu.fileId || !edu.file?.mimeType?.startsWith('image/')) return;
    setGalleryImages([
      {
        id: edu.fileId,
        thumbnail: edu.file.thumbnail,
        mimeType: edu.file.mimeType,
      },
    ]);
    setGalleryIndex(0);
    setGalleryOpen(true);
    imageGalleryRef.current?.handleOpenModal(0);
  };

  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleAddEducation = () => {
    setEditingEducation(null);
    setFormData({
      educationSchoolId: '',
      programTitle: '',
      completedDate: '',
    });
    setUploadedFileId(null);
    setFileName('');
    setDialogOpen(true);
  };

  const handleEditEducation = (education: Education) => {
    setEditingEducation(education);
    setFormData({
      educationSchoolId: education.educationSchoolId?.toString() || '',
      programTitle: education.programTitle,
      completedDate: formatDateForInput(education.completedDate),
    });
    setUploadedFileId(education.fileId || null);
    setFileName(education.file?.name || '');
    setDialogOpen(true);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingFile(true);

      // Step 1: Upload file to /files endpoint
      const result = await dataProvider.create('files', {
        data: {
          file,
          name: file.name,
          folder: 'driver-education',
          description: `Education file for driver ${driverData?.id}`,
        },
      });

      // Step 2: Store the fileId (not the raw file)
      const uploadedFileIdFromResponse = result.data?.id;
      setUploadedFileId(uploadedFileIdFromResponse);
      setFileName(file.name);
      notify('File uploaded successfully', { type: 'success' });
    } catch (error: any) {
      notify('Error uploading file: ' + (error.message || 'Unknown error'), { type: 'error' });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSaveEducation = async () => {
    if (!formData.programTitle || !formData.completedDate) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    // Safely convert educationSchoolId to number or null
    const getSchoolId = () => {
      if (!formData.educationSchoolId || formData.educationSchoolId === '') {
        return null;
      }
      const parsed = parseInt(formData.educationSchoolId as string, 10);
      return isNaN(parsed) ? null : parsed;
    };

    try {
      if (editingEducation) {
        const updateData: any = {
          educationSchoolId: getSchoolId(),
          programTitle: formData.programTitle,
          completedDate: formData.completedDate,
        };

        // Use uploadedFileId if a new file was uploaded, otherwise keep existing fileId
        if (uploadedFileId) {
          updateData.fileId = uploadedFileId;
        }

        await dataProvider.update('drivers/education', {
          id: editingEducation.id,
          data: updateData,
          previousData: editingEducation,
        });
        notify('Education record updated successfully', { type: 'success' });
      } else {
        const createData: any = {
          driverId: driverData?.id,
          educationSchoolId: getSchoolId(),
          programTitle: formData.programTitle,
          completedDate: formData.completedDate,
        };

        // Use uploadedFileId if a file was uploaded
        if (uploadedFileId) {
          createData.fileId = uploadedFileId;
        }

        await dataProvider.create('drivers/education', {
          data: createData,
        });
        notify('Education record added successfully', { type: 'success' });
      }
      setDialogOpen(false);
      onRefresh?.();
    } catch (error: any) {
      notify('Error saving education record: ' + (error.message || 'Unknown error'), { type: 'error' });
    }
  };

  const handleDeleteEducation = async (education: Education) => {
    if (window.confirm('Are you sure you want to delete this education record?')) {
      try {
        await dataProvider.delete('drivers/education', { id: education.id });
        notify('Education record deleted successfully', { type: 'success' });
        onRefresh?.();
      } catch (error: any) {
        notify('Error deleting education record: ' + (error.message || 'Unknown error'), { type: 'error' });
      }
    }
  };

  const handleDialogClose = () => {
    setFormData({
      educationSchoolId: '',
      programTitle: '',
      completedDate: '',
    });
    setUploadedFileId(null);
    setFileName('');
    setDialogOpen(false);
  };

  const getSchoolName = (schoolId?: number) => {
    if (!schoolId) return 'Not specified';
    return educationSchools.find(s => s.id === schoolId)?.name || `School ${schoolId}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Education</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={handleAddEducation}
        >
          Add Education
        </Button>
      </Box>

      {/* Table */}
      {educationRecords.length > 0 ? (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Program</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Completion Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {educationRecords.map(education => (
              <TableRow key={education.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <TableCell>{getSchoolName(education.educationSchoolId)}</TableCell>
                <TableCell>{education.programTitle}</TableCell>
                <TableCell>{new Date(education.completedDate).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEditEducation(education)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No education records added yet
        </Typography>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{editingEducation ? 'Edit Education' : 'Add Education'}</Typography>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, overflow: 'visible', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '6px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#555' } }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Driving School</InputLabel>
              <Select
                value={formData.educationSchoolId}
                onChange={(e) => setFormData({ ...formData, educationSchoolId: e.target.value })}
                label="Driving School"
              >
                <MenuItem value="">Select school</MenuItem>
                {educationSchools.map(school => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <MuiTextField
              size="small"
              label="Program Title"
              fullWidth
              value={formData.programTitle}
              onChange={(e) => setFormData({ ...formData, programTitle: e.target.value })}
              placeholder="e.g., Level 5 Driver Training"
            />

            <MuiTextField
              size="small"
              label="Completion Date"
              type="date"
              fullWidth
              required
              value={formData.completedDate}
              onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Attach Certificate/Document
              </Typography>

              {/* Show uploaded file with remove button */}
              {uploadedFileId && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>✓ {fileName}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingEducation?.file?.mimeType?.startsWith('image/') && (
                      <Button
                        size="small"
                        onClick={() => setViewingImage(editingEducation)}
                      >
                        View
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setUploadedFileId(null);
                        setFileName('');
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              )}

              {/* File upload area - only show if no file uploaded */}
              {!uploadedFileId && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: isUploadingFile ? 'wait' : 'pointer',
                    backgroundColor: isUploadingFile ? '#f0f0f0' : '#fafafa',
                    '&:hover': { backgroundColor: isUploadingFile ? '#f0f0f0' : '#f5f5f5' },
                    border: '2px dashed #ccc',
                    opacity: isUploadingFile ? 0.6 : 1,
                    pointerEvents: isUploadingFile ? 'none' : 'auto',
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    disabled={isUploadingFile}
                    style={{ display: 'none' }}
                    id="education-file-input"
                  />
                  <label htmlFor="education-file-input" style={{ cursor: isUploadingFile ? 'wait' : 'pointer', display: 'block' }}>
                    {isUploadingFile ? (
                      <Typography variant="body2" color="text.secondary">
                        Uploading file...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click to select a file (PDF, DOC, DOCX, JPG, PNG)
                      </Typography>
                    )}
                  </label>
                </Paper>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              handleDeleteEducation(editingEducation!);
              handleDialogClose();
            }}
            variant="outlined"
            color="error"
            sx={{ visibility: editingEducation ? 'visible' : 'hidden' }}
          >
            Delete
          </Button>
          <Box>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={handleSaveEducation} variant="contained">
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      <ImageGalleryModal
        ref={imageGalleryRef}
        images={galleryImages}
        imageDefaultIndex={galleryIndex}
        openModal={galleryOpen}
        setOpenModal={setGalleryOpen}
      >
        <></>
      </ImageGalleryModal>
    </Box>
  );
};
